<template>
    <div class="kallelsemallar">
        <modal-basic ref="dialogRef" />
        <div class="header">
            <h1>Kallelsemallar</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateKallelseTab">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa kallelsemall</span>
            </CustomButton>
        </div>

        <search-filter v-model="filterValues" :fields="filterFields" @input="handleSearch">
            <template v-slot:header-filters>
                <KText class="search-field" name="q" placeholder="Sök" />
            </template>
            <template v-slot:fields>
                <KText type="text" name="namn__icontains" label="Mall namn" />
                <KText type="text" name="typ" label="Typ av kallelsemall" />
                <ProfileAutocomplete
                    name="created_by"
                    label="Skapad av"
                    allowSearch
                    show-roll="yes"
                    @option:selected="onCreatedBySelected"
                />
            </template>
        </search-filter>

        <kliniken-table
            :items="kallelsemallar"
            :columns="columns"
            :count="listCount"
            :currentPage="listPage"
            :limit="listLimit"
            paginationStyle="paging"
            @sort="onSortChange"
            @loadPage="onLoadPage"
            @changeLimit="onChangeLimit"
            @redigeraKallelsemall="redigeraKallelsemall"
            @dupliceraKallelsemall="dupliceraKallelsemall"
            @raderaKallelsemall="raderaKallelsemall"
            :showBottom="true"
            :showLimitDropdown="true"
            :has-dropdown="true"
            :dropdown-actions="dropdownActions"
            showGreyBackground
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, toRefs } from "vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import SearchFilter from "@/components/SearchFilter.vue";
    import KallelsemallForm from "@/tabs/Operation/Kallelser/KallelsemallForm.vue";
    import { getErrorMessage } from "@/api";
    import { flashMessage, openDialog } from "@/utils";
    import EditIcon from "@/assets/actionIcons/edit.svg";
    import DeleteIcon from "@/assets/actionIcons/delete.svg";
    import DuplicateIcon from "@/assets/actionIcons/duplicate.svg";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import emitter from "tiny-emitter/instance";
    import { useStore } from "@/store";
    import ProfileAutocomplete from "@/components/forms/reusables/ProfileAutocomplete.vue";
    import type { Option } from "@/components/forms/inputs/Autocomplete/types";
    import CustomButton from "@/components/Button.vue";

    type DropdownAction = {
        name: string;
        title: string;
        icon: string;
        show: () => boolean;
    };

    type KallelsemallListItem = {
        id: number | string;
        namn?: string;
        typ?: string;
        typ_display?: string;
        updated_at?: string;
        created_by_display?: {
            user_display?: {
                name?: string;
            };
        };
    };

    const store = useStore();

    const props = defineProps<{ tabId: number }>();
    const { tabId } = toRefs(props);

    const dialogRef = ref<InstanceType<typeof ModalBasic> | null>(null);

    const filterValues = ref<Record<string, string>>({});
    const createdByTagOptions = ref<{ label: string; value: string }[]>([]);
    const columns = [
        {
            title: "Mall namn",
            name: "namn",
            sort: "namn",
        },
        {
            title: "Typ av kallelsemall",
            name: "typ_display",
            sort: "typ",
        },
        {
            title: "Senast anvand",
            name: "updated_at",
            type: "date",
        },
        {
            title: "Skapat av",
            name: "created_by_display.user_display.name",
            sort: "created_by",
        },
    ];

    const dropdownActions = ref<DropdownAction[]>([
        {
            name: "redigeraKallelsemall",
            title: "Redigera",
            icon: EditIcon,
            show: () => true,
        },
        {
            name: "dupliceraKallelsemall",
            title: "Duplicera",
            icon: DuplicateIcon,
            show: () => true,
        },
        {
            name: "raderaKallelsemall",
            title: "Radera",
            icon: DeleteIcon,
            show: () => true,
        },
    ]);
    const filterFields = computed(() => ({
        namn__icontains: { label: "Namn " },
        typ: { label: "Typ " },
        created_by: { label: "Skapat av ", options: createdByTagOptions.value },
        q: { label: "Sök " },
    }));

    const onCreatedBySelected = (option: unknown) => {
        const selectedOption = option as Option | null;

        if (!selectedOption?.value || typeof selectedOption.label !== "string") {
            return;
        }

        const selectedValue = String(selectedOption.value);

        createdByTagOptions.value = [
            { label: selectedOption.label, value: selectedValue },
            ...createdByTagOptions.value.filter((existingOption) => existingOption.value !== selectedValue),
        ];
    };

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<Record<string, string> | undefined>();
    const handleSearch = async (filters: Record<string, string> = {}) => {
        const normalizedFilters = { ...filters };

        if (normalizedFilters.q) {
            normalizedFilters.q = normalizedFilters.q.toLowerCase();
        }
        if (normalizedFilters.namn) {
            normalizedFilters.namn = normalizedFilters.namn.toLowerCase();
        }
        if (normalizedFilters.typ) {
            normalizedFilters.typ = normalizedFilters.typ.toLowerCase();
        }
        if (normalizedFilters.created_by) {
            normalizedFilters.created_by = normalizedFilters.created_by.toLowerCase();
        } else {
            createdByTagOptions.value = [];
        }

        filterValues.value = normalizedFilters;
        filterParams.value = Object.keys(normalizedFilters).length ? normalizedFilters : undefined;
        listPage.value = 1;
        await fetchKallelsemallar({ page: 1 });
    };

    const subsetName = ref<string>("default");

    const kallelsemallar = computed<KallelsemallListItem[]>(() =>
        store.getters["kallelsemall/kallelsemallSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const kallelsemallarNewlyCreated = computed<KallelsemallListItem[]>(() =>
        store.getters["kallelsemall/kallelsemallSubset"]({
            subsetName: "newlyCreated",
            offset: 0,
            limit: listLimit.value,
        })
    );

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: "Kallelsemallar",
        });
    };

    const openCreateKallelseTab = async () => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", KallelsemallForm);
        store.dispatch("tabs/openTab", {
            component: KallelsemallForm,
        });
    };

    const redigeraKallelsemall = async (options: Record<string, unknown> = {}) => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", KallelsemallForm);
        store.dispatch("tabs/openTab", {
            component: KallelsemallForm,
            parameters: { ...options, isEditing: true },
        });
    };

    const raderaKallelsemall = async ({ id }: { id?: number | string }) => {
        if (!id) {
            throw new Error("Missing id");
        }

        const confirm = await dialogRef.value?.show({
            variant: BasicModalVariant.WARNING,
            title: "Vill du verkligen radera hela kallelsemallen?",
            okButton: "Ja, radera",
            cancelButton: "Nej, avbryt",
        });

        if (!confirm) {
            return;
        }

        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            const response = await store.dispatch("kallelsemall/deleteKallelsemall", { id });
            if (response?.status === 204) {
                flashMessage("Kallelsemall raderad");
                await fetchKallelsemallar({ page: listPage.value });

                emitter.emit("kallelsemall:changed");
            }
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const dupliceraKallelsemall = async (options: Record<string, unknown> = {}) => {
        const { id, ...rest } = options;

        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", KallelsemallForm);
        store.dispatch("tabs/openTab", {
            component: KallelsemallForm,
            parameters: { ...rest, isDuplicating: true },
        });
    };

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;
        await fetchKallelsemallar({ page: 1, limit: newLimit });
    };

    const onLoadPage = async (page: number) => {
        await fetchKallelsemallar({ page });
    };

    const onSortChange = async (newSortBy: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: newSortBy,
        };
        await fetchKallelsemallar({ page: 1 });
    };

    const fetchKallelsemallar = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("kallelsemall/fetchKallelsemallar", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (typeof total === "number") {
                listCount.value = total;
            }

            return nextPage;
        } catch (error) {
            console.log(error);
            openDialog("Kunde inte hamta kallelsemallar", "error");
        }
    };

    const onKallelsemallUpdated = () => fetchKallelsemallar({ page: listPage.value });

    onMounted(async () => {
        await fetchKallelsemallar({ page: listPage.value });
        emitter.on("kallelsemall:changed", onKallelsemallUpdated);
        setTabName();
    });

    onBeforeUnmount(() => {
        emitter.off("kallelsemall:changed", onKallelsemallUpdated);
    });
</script>

<style lang="scss" scoped>
    .kallelsemallar {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }
    }
</style>
